/**
 * Remark plugin that converts [n] patterns in text into citation nodes.
 *
 * Only processes text nodes — patterns inside code blocks, links, etc.
 * are left untouched because remark respects AST node boundaries.
 *
 * Citation nodes use hName/hProperties so rehype renders them as
 * <citation number="n"> elements, which the markdown component map
 * renders as CitationBubble components.
 */
import { visit } from 'unist-util-visit'
import type { Root, Text, PhrasingContent } from 'mdast'
import { CITATION_PATTERN } from '#app/utils/citations.js'

// Match [1], [2], ..., [999]
const CITATION_RE = new RegExp(CITATION_PATTERN, 'g')

export function remarkCitations() {
	return (tree: Root) => {
		visit(tree, 'text', (node: Text, index: number | undefined, parent) => {
			if (!parent || index == null) return

			const matches = [...node.value.matchAll(CITATION_RE)]
			if (matches.length === 0) return

			const children: PhrasingContent[] = []
			let lastIndex = 0

			for (const match of matches) {
				const matchStart = match.index!
				const matchEnd = matchStart + match[0].length

				// Text before the citation
				if (matchStart > lastIndex) {
					children.push({
						type: 'text',
						value: node.value.slice(lastIndex, matchStart),
					})
				}

				// Citation node — uses hName/hProperties for rehype
				const number = parseInt(match[1], 10)
				children.push({
					type: 'text',
					value: '',
					data: {
						hName: 'citation',
						hProperties: { number },
					},
				} as unknown as PhrasingContent)

				lastIndex = matchEnd
			}

			// Text after the last citation
			if (lastIndex < node.value.length) {
				children.push({
					type: 'text',
					value: node.value.slice(lastIndex),
				})
			}

			// Replace the node with our split children
			parent.children.splice(index, 1, ...(children as typeof parent.children))
		})
	}
}
